// @flow
import type {Element} from 'react'
import React, {useState, useRef} from 'react'
import classNames from 'classnames'

import type {DeleteCallback, Path, PickAlert, UpdateCallback} from 'components/proposals/types'
import {type FakeOnKeyDownEvent} from 'components/ui/CKEditor'
import type {AiSuggestProps} from 'components/ai/AiSuggest/AiSuggestion'

import {emptyValue, ValueInput} from '..'
import type {Circle, Organization, ValidationResult} from '../ValueInput'
import Value from '../Value'
import DeleteButton from './DeleteButton'
import EnterNotice from './EnterNotice'
import styles from './index.scss'

type EnterOverride = 'NEWLINE' | 'NEW_ITEM'

type Props = $ReadOnly<{
  addItemTitle: string,
  circle: ?Circle,
  className: string,
  disabled: boolean,
  enterOverride: EnterOverride,
  changeEnterOverride: EnterOverride => void,
  isReadonly: boolean,
  label: React$Node,
  onChange: UpdateCallback,
  onDelete: DeleteCallback,
  organization: Organization,
  path: Path,
  pickAlert: PickAlert,
  strikethrough: boolean,
  validate: ?(value: string) => ?ValidationResult,
  values: Value[],
  placeholder: string,
  aiSuggestProps?: AiSuggestProps | void
}>

function AccordianField(props: Props): Element<"div"> {
  const [inputIndex, setInputIndex] = useState<number>(0)
  const nextInputRef = useRef<any | null>(null)

  const accordianFieldClassName = classNames(styles.accordianField, props.className)

  const isDisabled = props.disabled || props.isReadonly

  const addItem = () => {
    if (isDisabled)
      return

    props.onChange(
      props.path.concat(props.values.length),
      emptyValue(),
    )
  }

  const saveCurrentInputIndex = (index: number) => () => {
    setInputIndex(index)
  }

  const focusNextInputRef = () => {
    nextInputRef.current?.focus()
  }

  const moveToNext = (index: number) => {
    const lastItemIndex = props.values.length - 1
    if (index === lastItemIndex)
      addItem()
    else
      focusNextInputRef()
  }

  const enterOverrideCallback = (
    index: number,
    event: SyntheticKeyboardEvent<HTMLTextAreaElement> | FakeOnKeyDownEvent,
  ) => {
    const shouldMoveToNext = (props.enterOverride === 'NEW_ITEM' && !event.shiftKey)
      || (props.enterOverride === 'NEWLINE' && event.shiftKey)

    if (shouldMoveToNext) {
      moveToNext(index)
      event.preventDefault()
    }
  }

  const onKeyDown = (index: number) => (
    (event: SyntheticKeyboardEvent<HTMLTextAreaElement> | FakeOnKeyDownEvent) => {
      if (event.key !== 'Enter')
        return

      enterOverrideCallback(index, event)
    }
  )

  const isNextInput = (index: number) => (inputIndex + 1) === index

  const renderDeleteButton = (path: Path, isProtected: boolean) => {
    if (props.isReadonly || isProtected)
      return null
    return <DeleteButton path={path} onClick={props.onDelete} />
  }

  const renderField = (value: Value, index: number) => {
    const valuePath = props.path.concat(index)
    const alertPath = `${props.path[0]}/${value.databaseId || index}`
    const inputAlert = props.pickAlert(alertPath)
    const id = `accordian-field-${valuePath.join('-')}`
    const isLastIndex = index === props.values.length - 1

    return (
      <ValueInput
        autofocus={isLastIndex}
        alert={inputAlert}
        circle={props.circle}
        disabled={props.disabled}
        id={id}
        isReadonly={props.isReadonly || value.isProtected}
        key={index}
        onChange={props.onChange}
        organization={props.organization}
        path={valuePath}
        rightButton={renderDeleteButton(valuePath, value.isProtected)}
        strikethrough={props.strikethrough}
        textarea
        validate={props.validate}
        value={value}
        onKeyDown={onKeyDown(index)}
        onFocus={saveCurrentInputIndex(index)}
        ref={isNextInput(index) ? nextInputRef : null}
        placeholder={props.placeholder}
        aiSuggestProps={props.aiSuggestProps}
      />
    )
  }

  return (
    <div className={accordianFieldClassName}>
      <div className="refinery-accordian-field__top">
        <div className="label label-default">
          {props.label}
        </div>
      </div>
      {props.values.map(renderField)}
      {!isDisabled && (
        <div className={styles.bottomRow}>
          <div>
            <button
              className="btn btn-outline-primary btn-sm js-add-item mr-2"
              onClick={addItem}
              type="button"
            >
              {props.addItemTitle}
            </button>
          </div>
          {props.values.length > 0 && (
            <div className={styles.enterNotice}>
              <EnterNotice
                enterOverride={props.enterOverride}
                changeEnterOverride={props.changeEnterOverride}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

AccordianField.defaultProps = {
  circle: null,
  className: '',
  disabled: false,
  isReadonly: false,
  strikethrough: false,
  validate: null,
  placeholder: (I18n.t('proposals.rewrite.unspecified'): string),
}

export {emptyValue}
export type {Circle, Organization}

export default AccordianField
